import { inject, Injectable, OnDestroy, Signal } from '@angular/core';
import { Params, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  pairwise,
  ReplaySubject,
  Subscription,
  take,
  combineLatest,
  startWith,
  scan,
} from 'rxjs';

import { isPresent, equals, unique, unsubscribe, noop } from '@fcom/core/utils';
import { globalBookingLocations, globalBookingRouteType } from '@fcom/common/store/selectors/global-booking.selectors';
import { GlobalBookingTravelClass, isNotEmpty } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { getBookingStepLink } from '@fcom/common/utils/booking-common.utils';
import {
  GlobalBookingActions,
  globalBookingFlights,
  globalBookingPaxAmount,
  selectGlobalBookingState,
  GlobalBookingState,
  globalBookingTravelClasses,
  selectGlobalBookingTripType,
  lastRequestParams,
  LocationPair,
  selectFareFamiliesBrands,
} from '@fcom/common/store';
import { FlightSegment, OfferListFetchParams, Step } from '@fcom/common/interfaces';
import { TripType } from '@fcom/core/interfaces';
import { GlobalBookingFlight } from '@fcom/common/store/store.interface';
import { CartStatus, PaxAmount } from '@fcom/dapi';
import { LocationsService } from '@fcom/locations';
import { finShare, snapshot } from '@fcom/rx';
import { Location, LocationRouteType } from '@fcom/core-api/interfaces';
import { AVAILABLE_TRIP_TYPES, GLOBAL_BOOKING_WIDGET_EXPANDER_KEY } from '@fcom/booking-widget/constants';
import { BOOKING_STEPS, BOOKING_STEPS_ARRAY } from '@fcom/common/config/booking-config';
import { BookingQueryParams } from '@fcom/common/interfaces/booking/booking-step.interface';
import { BookingAndAppState, TravelClasses } from '@fcom/common/interfaces/booking';
import { createParamsForStep } from '@fcom/common/utils/create-params-for-step';
import { ExpanderStatus, WidgetTab } from '@fcom/booking-widget/interfaces';
import { BookingWidgetActions } from '@fcom/booking-widget/store/actions';
import { PseudoLocalActions } from '@fcom/core/actions';
import { Category, FinnairCart, FinnairServiceCatalogEligibilityReason } from '@fcom/dapi/api/models';

import { BookingActions, CartActions } from '../store/actions';
import { bookingStep, cartData, cartStatusSelector, offerLastRequestParams } from '../store/selectors';

export interface StepChangeSignature {
  onSuccessPost?: (step: Step) => void;

  /**
   * If this is set to true, will not call router.navigate
   */
  skipRouteChange?: boolean;
}

export interface SetStepSignature extends StepChangeSignature {
  step: Step;
  locale?: string;
}

interface TranslationKeyFareFamilyBransDictiounaryItem {
  translationKey: string;
  brandNames: string[];
}

@Injectable({ providedIn: 'root' })
export class BookingService implements OnDestroy {
  private store$ = inject<Store<BookingAndAppState>>(Store);
  private languageService = inject(LanguageService);
  private locationsService = inject(LocationsService);
  private router = inject(Router);
  private readonly globalBookingFlights$: Observable<GlobalBookingFlight[]>;
  private readonly step$: Observable<Step>;
  private readonly previousStep$: ReplaySubject<Step> = new ReplaySubject(2);
  private readonly paxAmount$: Observable<PaxAmount>;
  private readonly tripType$: Observable<TripType>;
  private readonly travelClasses$: Observable<TravelClasses>;
  private readonly routeType$: Observable<LocationRouteType>;
  private readonly cart$: Observable<FinnairCart>;
  private readonly shouldShowOwnBagsStepObservable$: Observable<boolean>;
  private readonly shouldShowOwnBagsStepSignal: Signal<boolean>;

  private readonly shortHaulFlightWeights = {
    'fragments.lightTicketWarning': 20,
    'fragments.superLightTicketWarning': 30,
    'fragments.businessLightTicketWarning': 10,
  };

  private readonly longHaulFlightWeights = {
    'fragments.lightTicketWarning': 20,
    'fragments.superLightTicketWarning': 10,
    'fragments.businessLightTicketWarning': 30,
  };

  private readonly superLightFareFamiliesDictionary: TranslationKeyFareFamilyBransDictiounaryItem[] = [
    {
      translationKey: 'fragments.lightTicketWarning',
      brandNames: [
        'Economy Light',
        'Economy Campaign Light',
        'Economy Light Corporate',
        'Premium Economy Light',
        'Premium Economy Light Corporate',
        'Premium Economy Campaign Light',
        'Premium Economy Campaign Light Corporate',
      ],
    },
    {
      translationKey: 'fragments.superLightTicketWarning',
      brandNames: ['Economy Superlight', 'Economy Superlight Campaign', 'Economy Superlight Corporate'],
    },
    {
      translationKey: 'fragments.businessLightTicketWarning',
      brandNames: ['Business Campaign Light', 'Business Light Corporate', 'Business Light'],
    },
  ];

  private subscriptions = new Subscription();

  constructor() {
    this.step$ = this.store$.pipe(bookingStep());
    this.step$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.id === curr.id),
        pairwise(),
        map(([previousStep]) => previousStep)
      )

      .subscribe((step) => this.previousStep$.next(step));
    this.paxAmount$ = this.store$.pipe(globalBookingPaxAmount());
    this.tripType$ = this.store$.select(selectGlobalBookingTripType);
    this.travelClasses$ = this.store$.pipe(globalBookingTravelClasses());
    this.globalBookingFlights$ = this.store$.pipe(globalBookingFlights());
    this.routeType$ = this.store$.pipe(globalBookingRouteType());
    this.cart$ = this.store$.pipe(cartData());

    this.fetchLocationInformationBasedOnSelections();

    const bagsCategories = new Set([Category.CABIN_BAGGAGE, Category.BAGGAGE, Category.SPORT]);

    this.shouldShowOwnBagsStepObservable$ = combineLatest([
      this.cart$.pipe(startWith(undefined)),
      this.step$,
      this.store$.select(cartStatusSelector),
    ]).pipe(
      filter(([, , status]) => status === CartStatus.OK || status === CartStatus.INITIAL),
      scan((acc, [cart, step]) => {
        if (step.id < BOOKING_STEPS.PAXDETAILS.id) {
          return true;
        } else if ((step.id === BOOKING_STEPS.PAXDETAILS.id && !acc) || isPresent(cart?.serviceCatalog?.categories)) {
          return Boolean(cart?.serviceCatalog?.categories?.some((item) => bagsCategories.has(item.category)));
        }

        return acc;
      }, true),
      distinctUntilChanged(),
      finShare()
    );

    this.shouldShowOwnBagsStepSignal = toSignal(this.shouldShowOwnBagsStepObservable$, { initialValue: false });
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  get step(): Observable<Step> {
    return this.step$;
  }

  get shouldShowOwnBagsStep(): boolean {
    return this.shouldShowOwnBagsStepSignal();
  }

  get shouldShowOwnBagsStep$(): Observable<boolean> {
    return this.shouldShowOwnBagsStepObservable$;
  }

  get steps(): Step[] {
    const cart = snapshot(this.cart$);
    return this.cartHasCorporateSeatBenefit(cart)
      ? BOOKING_STEPS_ARRAY.filter(
          (step) =>
            step !== BOOKING_STEPS.SEATS && !(!this.shouldShowOwnBagsStepSignal() && step === BOOKING_STEPS.BAGS)
        )
      : BOOKING_STEPS_ARRAY.filter((step) => !(!this.shouldShowOwnBagsStepSignal() && step === BOOKING_STEPS.BAGS));
  }

  get currentStep(): Step {
    return snapshot(this.store$.pipe(bookingStep()));
  }

  get previousStep(): Observable<Step> {
    return this.previousStep$.asObservable();
  }

  get globalBookingSelections(): GlobalBookingState {
    return snapshot(this.store$.select(selectGlobalBookingState));
  }

  get locations(): Observable<GlobalBookingFlight[]> {
    // TODO: clear (in reducer) and fetch locations if missing, e.g., when changing language
    // see https://github.com/FinnairOyj/finnair-com-ux/issues/2208
    // TODO 2023-10-27: is the above still valid after moving locations from booking to common?
    return this.globalBookingFlights$;
  }

  get paxAmount(): Observable<PaxAmount> {
    return this.paxAmount$;
  }

  get travelClasses(): Observable<TravelClasses> {
    return this.travelClasses$.pipe(finShare());
  }

  get tripType(): Observable<TripType> {
    return this.tripType$.pipe(finShare());
  }

  get offerListParams(): OfferListFetchParams {
    return snapshot(this.store$.pipe(lastRequestParams())) ?? snapshot(this.store$.pipe(offerLastRequestParams()));
  }

  get routeType(): Observable<LocationRouteType> {
    return this.routeType$.pipe(finShare());
  }

  setRouteType(routeType: LocationRouteType): void {
    this.store$.dispatch(GlobalBookingActions.setRouteType({ routeType }));
  }

  setFlights(flights: GlobalBookingState['flights']): void {
    this.store$.dispatch(
      GlobalBookingActions.setFlights({
        flights,
      })
    );
  }

  setFlightsFromLocationsCodes(flightSearch: FlightSegment[]): void {
    this.subscriptions.add(
      this.getLocationsFromFlights(flightSearch).subscribe((locations) => {
        this.setFlights(
          flightSearch.map((flight) => ({
            origin: locations[flight.origin],
            destination: locations[flight.destination],
            departureDate: flight.departureDate,
          }))
        );
      })
    );
  }

  getLocationsFromFlights(flightSearch: FlightSegment[]): Observable<Record<string, Location>> {
    const uniqueListOfLocations = flightSearch
      .reduce((arr, flight) => {
        if (!arr.includes(flight.origin)) {
          arr.push(flight.origin);
        }

        if (!arr.includes(flight.destination)) {
          arr.push(flight.destination);
        }

        return arr;
      }, [])
      .filter(Boolean);

    return this.locationsService.triggerLocationFetchAll(uniqueListOfLocations).pipe(take(1));
  }

  setStep({ step, onSuccessPost = noop, skipRouteChange = false, locale }: SetStepSignature): void {
    this.goToStep({ step, skipRouteChange, onSuccessPost, locale });
  }

  prevStep(): void {
    const nextStep = this.currentStepIndex - 1;

    if (nextStep >= 0 && this.currentStepIndex !== this.getStepIndex(BOOKING_STEPS.PURCHASE_SUCCESS)) {
      this.setStep({
        step: this.steps[nextStep],
      });
    }
  }

  nextStep(): void {
    const nextStep = this.currentStepIndex + 1;

    if (nextStep < this.steps.length) {
      this.setStep({
        step: this.steps[nextStep],
      });
    }
  }

  isFirstStep(): Observable<boolean> {
    return this.step.pipe(map((s) => s.id === BOOKING_STEPS.ENTRY.id));
  }

  setPaxAmount(paxAmount: PaxAmount): void {
    if (!equals(paxAmount, snapshot(this.store$.pipe(globalBookingPaxAmount())))) {
      this.store$.dispatch(GlobalBookingActions.setPaxAmount({ paxAmount }));
    }
  }

  setActiveTab(tab: WidgetTab): void {
    this.store$.dispatch(BookingWidgetActions.setActiveTab({ activeTab: tab }));
  }

  increasePaxAmountField(field: string, value: number): void {
    this.store$.dispatch(GlobalBookingActions.increasePaxAmountField({ field, increment: value }));
  }

  decreasePaxAmountField(field: string, value: number): void {
    this.store$.dispatch(GlobalBookingActions.decreasePaxAmountField({ field, decrement: value }));
  }

  setTripType(tripType: TripType): void {
    if (tripType !== snapshot(this.store$.select(selectGlobalBookingTripType))) {
      this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));
    }
  }

  selectTravelClass(travelClass: GlobalBookingTravelClass): void {
    this.store$.dispatch(GlobalBookingActions.setTravelClass({ travelClass }));
  }

  updateTripTypeBasedOnQueryParams(queryParams: Params): void {
    const tripType = queryParams[BookingQueryParams?.tripType];

    if (tripType && AVAILABLE_TRIP_TYPES.includes(tripType)) {
      this.setTripType(tripType);
    }
  }

  getContentUrlTranslationKeySuperLightModalWindow(isShortHaulFlight: boolean): string {
    const selectedFareFamiliesBrands = snapshot(this.store$.pipe(selectFareFamiliesBrands()));

    return this.superLightFareFamiliesDictionary
      .filter((dictionary) => {
        if (selectedFareFamiliesBrands.filter((brand) => dictionary.brandNames.includes(brand)).length > 0) {
          return dictionary;
        }
      })
      .reduce((prev, current) => {
        const weights = isShortHaulFlight ? this.shortHaulFlightWeights : this.longHaulFlightWeights;
        return weights[prev.translationKey] > weights[current.translationKey] ? prev : current;
      }, {} as TranslationKeyFareFamilyBransDictiounaryItem)?.translationKey;
  }

  setGlobalBookingWidgetExpanderStatus(expandStatus: ExpanderStatus): void {
    this.store$.dispatch(
      PseudoLocalActions.setValue({
        key: GLOBAL_BOOKING_WIDGET_EXPANDER_KEY,
        data: `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${expandStatus}`,
      })
    );
  }

  cartHasCorporateSeatBenefit(cart: FinnairCart, flightOrBoundId?: string): boolean {
    if (!cart) {
      return false;
    }
    const seatCategory = cart.eligibilities?.serviceCatalog?.categories?.find(
      ({ category }) => category === Category.SEAT
    );
    const seatServices = seatCategory?.services ? Object.entries(seatCategory.services) : [];
    if (!seatServices.length) {
      return false;
    }
    const flightEligibilities = seatServices
      .filter(([serviceFlightId]) => !flightOrBoundId || serviceFlightId === flightOrBoundId)
      .flatMap(([_, eligibility]) => Object.values(eligibility));
    if (!flightEligibilities?.length) {
      return false;
    }

    return flightEligibilities.every((eligibility) => {
      return (
        !eligibility.isAllowedToUse &&
        (eligibility.reason ===
          FinnairServiceCatalogEligibilityReason.PURCHASE_SEAT_SELECTION_NOT_ELIGIBLE_WHEN_DISCOUNTS_EXIST_FOR_CORPORATE ||
          eligibility.reason === FinnairServiceCatalogEligibilityReason.PURCHASE_NOT_ELIGIBLE_FOR_OTHER_AIRLINE)
      );
    });
  }

  private get currentStepIndex(): number {
    return this.getStepIndex(this.currentStep);
  }

  private goToStep({ step, onSuccessPost = noop, skipRouteChange = false, locale }: SetStepSignature): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Check  if returning to flight selection or earlier from a later phase
      const isClearCart =
        step.phase <= BOOKING_STEPS.FLIGHT_SELECTION.phase && this.currentStep && this.currentStep.phase > step.phase;

      if (!skipRouteChange) {
        this.router
          .navigateByUrl(
            getBookingStepLink(
              locale || this.languageService.langValue,
              step,
              createParamsForStep(step, this.globalBookingSelections, this.offerListParams)
            ),
            { onSameUrlNavigation: 'reload' }
          )
          .then((b) => {
            if (b) {
              this.store$.dispatch(BookingActions.setStep({ step }));
              resolve();
            } else {
              reject();
            }
          })
          .catch(reject)
          .finally(() => {
            if (isClearCart) {
              // Clear cart if returning to flight selection or earlier from a later phase
              this.store$.dispatch(CartActions.reset());
            }
          });
      } else {
        if (isClearCart) {
          // Clear cart if returning to flight selection or earlier from a later phase
          this.store$.dispatch(CartActions.reset());
        }
        this.store$.dispatch(BookingActions.setStep({ step }));
        resolve();
      }
    })
      .then(() => onSuccessPost(step))
      .catch(noop);
  }

  private getStepIndex(step: Step): number {
    return this.steps.findIndex((s) => s.id === step.id);
  }

  private fetchLocationInformationBasedOnSelections() {
    this.subscriptions.add(
      this.store$
        .pipe(
          globalBookingLocations(),
          filter((locations: LocationPair[]) => !!locations?.length),
          distinctUntilChanged((prev, next) =>
            next.every(
              (loc, i) =>
                loc?.origin?.locationCode === prev?.[i]?.origin?.locationCode &&
                loc?.destination?.locationCode === prev?.[i]?.destination?.locationCode
            )
          ),
          map((locations) =>
            locations
              .flatMap((location) => [location.origin?.locationCode, location.destination?.locationCode])
              .filter(unique)
              .filter(Boolean)
          ),
          filter(isNotEmpty)
        )
        .subscribe((locationCodes: string[]) => {
          locationCodes.forEach((code) => {
            this.locationsService.triggerLocationFetch(code);
          });
        })
    );
  }
}
